/* eslint-disable @typescript-eslint/no-explicit-any */
import { Location } from "history";
import { match, RouteProps } from "react-router-dom";
import { Saga } from "redux-saga";

import { FeatureGateValue, RouteUhfConfig } from "../models";
import { BaseState } from "../redux";

// The RouteConstraint array type variant allows it operate on an OR basis, where
// given two or more constraints, one valid constraint is enough to make the whole group valid.
export type RouteConstraint =
  | {
      featureGates?: {
        // Allows the consumer to define a function to validate the value of a feature gate
        [key: string]: (featureGateValue: FeatureGateValue) => boolean;
      };
      locale?: string;
    }
  | RouteConstraint[];

export type RouteTestNavConfig = Readonly<{
  title: string;
  path: string;
}>;

export type RouteTestConfig = Readonly<{
  title?: string;
  testPath?: string | RouteTestNavConfig[];
}>;

export type LazyRouteConfig<TRootState extends BaseState> = {
  /**
   * Route dependent data fetching. Runs once per route load.
   */
  fetchDataSaga?: Saga<[RouteConfigMatch<TRootState>, Location]>;

  /**
   * Route dependent partner level saga to run once per partner load.
   */
  bootstrapClientEnvironmentSaga?: Saga;
};

/**
 * Given an object of optional members, one must be non-optional.
 */
type RequireOne<T, Key = keyof T> = Exclude<
  {
    [K in keyof T]: K extends Key ? Omit<T, K> & Required<Pick<T, K>> : never;
  }[keyof T],
  undefined
>;

export interface IRouteConfig<
  TRootState extends BaseState,
  TLazyRouteConfig extends LazyRouteConfig<TRootState> = LazyRouteConfig<TRootState>,
> extends RouteProps {
  key: string;
  constraint?: RouteConstraint;
  testConfig?: RouteTestConfig;
  enableXboxCart?: boolean;
  uhfConfig?: RouteUhfConfig;

  /**
   * Load this path server side when navigating to it
   */
  isServerOnly?: boolean;

  requiresNewRequest?: (
    matchPathSchema: RouteConfig["path"],
    newLocation: Location,
    oldLocation?: Location
  ) => boolean;

  formatPageId?: (routeMatch: RouteConfigMatch, locale: string) => string;

  /**
   * Lazy route import initialization logic including but not limited to data fetching.
   */
  lazyConfig?: () => Promise<RequireOne<TLazyRouteConfig>>;
}

export type RouteConfig<TRootState extends BaseState = any> = Readonly<IRouteConfig<TRootState>>;

export enum RouteParam {
  WishlistId = "wishlistId",
  ProductName = "productName",
  ProductId = "productId",
  AvailabilityId = "availabilityId",
  SkuId = "skuId",
  Path = "path",
  NetworkName = "networkName",
  TabName = "tabName",
  ChannelId = "channelId",
  Xuid = "xuid",
}

export type RouteParameters = Readonly<{ [K in RouteParam]: string }>;

export type RouteConfigMatch<
  TRootState extends BaseState = any,
  TRouteParameters extends RouteParameters = any,
> = RouteConfig<TRootState> &
  Readonly<{
    match: match<TRouteParameters>;
  }>;
